// React imports
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

// Third-party library imports
import { yupResolver } from "@hookform/resolvers/yup";

// Utility and hook imports
import { classes } from "../../../../util/style-utils";
import useTitle from "../../../../hooks/useTitle";
import { clearSession } from "../../../../api/onboarding";

// Layout and shared components
import GeaoLayout from "../shared/Layout/GeaoLayout";

// GDS components
import InputBox from "../../../gds/InputBox";
import RadioButtonGroup from "../../../gds/RadioButtonGroup";
import SelectBox from "../../../gds/SelectBox";

// GDK components
import MultiSelectBox from "../../../gdk/MultiSelectBox";

// Local components and hooks
import AgencyNameField from "./AgencyNameField";
import AllowedStateAndLobAlerts from "./AllowedStateAndLobAlerts";
import CaptiveAgentQuestions from "./CaptiveAgentQuestions";
import CreateYourProfileFormFooter from "./CreateYourProfileFormFooter";
import EoPolicyQuestions from "./EoPolicyQuestions";
import ErrorAlerts from "./ErrorAlerts";
import FeinField from "./FeinField";
import NiprNameChangeModal from "./NiprNameChangeModal";
import PersonalInformationFields from "./PersonalInformationFields";
import useCreateYourProfileNavigation from "./useCreateYourProfileNavigation";
import useInterviewInitializer from "./useInterviewInitializer";
import { validationSchema } from "./validation";

// Constants and options
import RoleOptions from "../../constants/options/RoleOptions";
import { UsStateOptions } from "../../../../constants/options/UsStateOptions";
import YesNoOptions from "../../../../constants/options/YesNoOptions";
import useGtmTracking from "../../../../hooks/useGtmTracking";
import { ClientConfigurationContext } from "../../../../context/ClientConfigurationContextProvider";

export default function CreateYourProfile() {
    useTitle("Let's Get Started");
    useGtmTracking("Pre-onboarding ", "preonboarding_in_progress");

    const clientConfiguration = useContext(ClientConfigurationContext);

    const enableOffshore = clientConfiguration?.enableOffshore;

    const [niprNameChangeAccepted, setNiprNameChangeAccepted] = useState(false);

    const useFormReturn = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            offshoreResident: "false",
            mobilePhoneCountryCode: "+1",
            businessPhoneCountryCode: "+1"
        }
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setError,
        setValue,
        control
    } = useFormReturn;

    const {
        initializeInterviews,
        initializing,
        duplicateProfile,
        npnNotFoundInPdb,
        feinNotFoundInPdb,
        producerNameMismatch,
        agencyNameMismatch,
        duplicateAgent,
        duplicateAgency,
        niprAgentNameChange,
        niprAgencyNameChange,
        agentAssessmentKickout,
        agencyAssessmentKickout,
        parentAgencyMustOnboard,
        agencyOffshoreMustBeApproved
    } = useInterviewInitializer(setNiprNameChangeAccepted);

    useCreateYourProfileNavigation(
        duplicateAgency,
        duplicateAgent,
        niprAgencyNameChange,
        niprAgentNameChange,
        niprNameChangeAccepted,
        agencyAssessmentKickout,
        agentAssessmentKickout,
        parentAgencyMustOnboard,
        agencyOffshoreMustBeApproved
    );

    const watchRole = watch("role");
    const watchOffshoreResident = watch("offshoreResident");
    const isOffshoreResident = String(watchOffshoreResident) === "true";

    useEffect(() => { register("interestedLob") }, []);

    useEffect(() => {
        if (enableOffshore && isOffshoreResident) {
            setValue("role", "csr");
        }
    }, [isOffshoreResident, enableOffshore, setValue]);


    function onSubmit(values) {
        clearSession(() => { initializeInterviews(values); });
    }

    return (
        <GeaoLayout header="Let's Get Started">
            <div style={{ width: "750px" }}>
                <ErrorAlerts
                    duplicateProfile={duplicateProfile}
                    npnNotFoundInPdb={npnNotFoundInPdb}
                    feinNotFoundInPdb={feinNotFoundInPdb}
                    producerNameMismatch={producerNameMismatch}
                    agencyNameMismatch={agencyNameMismatch}
                    parentAgencyMustOnboard={parentAgencyMustOnboard}
                    agencyOffshoreMustBeApproved={agencyOffshoreMustBeApproved}
                    setError={setError}
                />
            </div>

            <FormProvider {...useFormReturn}>
                <form className="form geao-form" onSubmit={handleSubmit(onSubmit)} style={{ width: "750px" }} >
                    <h2 className="margin-bottom">General Information</h2>

                    <PersonalInformationFields />

                    <div className="flex-row">
                        {(!enableOffshore || !isOffshoreResident) && (
                            <SelectBox
                                control={control}
                                name="role"
                                label={<>What role do you play in the agency?<span className="required">*</span></>}
                                options={RoleOptions}
                                placeholder="Please Select"
                            />
                        )}

                        <div className="form-field">
                            <InputBox
                                control={control}
                                mask="000000000"
                                name="npn"
                                fullWidth
                                label={<>Individual National Producer Number (NPN){!['csr', 'authorized-appointment-rep', 'other-employee'].includes(watchRole) && <span className="required">*</span>}</>}
                            />
                        </div>
                    </div>

                    <h2 className="margin-bottom">Agency Information</h2>

                    <AgencyNameField />

                    <div className="flex-row">
                        <RadioButtonGroup trackingId="CreateYourProfile:SoleProprietor"
                            control={control}
                            name="soleProprietor"
                            legend={<>Are you a Sole Proprietor?<span className="required">*</span></>}
                            options={YesNoOptions}
                            layout="Two Column"
                        />
                    </div>

                    <FeinField />

                    <div className="flex-row">
                        <fieldset className={classes("form-field checkbox-group", errors.interestedLob && "form-field--error")} role="radiogroup">
                            <legend>
                                What lines of business are you interested in writing with GEICO?<span className="required">*</span>
                            </legend>
                            <div className="checkbox-wrapper col-2">
                                <div>
                                    <input data-onboarding-checkbox-id="CreateYourProfile:PersonalLines" id="lob-checkbox-ppa" type="checkbox" {...register("lobPersonal")} />
                                    <label htmlFor="lob-checkbox-ppa" className="checkbox"><span>Personal</span></label>
                                </div>
                                <div>
                                    <input data-onboarding-checkbox-id="CreateYourProfile:CommercialLines" id="lob-checkbox-commercial" type="checkbox" {...register("lobCommercial")} />
                                    <label htmlFor="lob-checkbox-commercial" className="checkbox"><span>Commercial</span></label>
                                </div>
                            </div>
                            {errors.interestedLob &&
                                <span className="form-message">{errors.interestedLob.message}</span>
                            }
                        </fieldset>
                    </div>

                    <div className="flex-row">
                        <MultiSelectBox
                            control={control}
                            name="geicoAppointedStates"
                            label={<>
                                What state(s) are you applying to be appointed in?{/*
                            */}<span className='required'>*</span>
                            </>}
                            descriptiveText="Select all options that apply."
                            options={UsStateOptions}
                        />
                    </div>

                    <AllowedStateAndLobAlerts />

                    <CaptiveAgentQuestions />

                    <EoPolicyQuestions />

                    <CreateYourProfileFormFooter initializing={initializing} />
                </form>
            </FormProvider>

            <NiprNameChangeModal
                niprAgentNameChange={niprAgentNameChange}
                niprAgencyNameChange={niprAgencyNameChange}
                setNiprNameChangeAccepted={setNiprNameChangeAccepted}
            />
        </GeaoLayout>
    );
}